"use client"

import type React from "react"
import { Alert, Box, Button, CircularProgress, Grid } from "@mui/material"
import { useForm, Controller, type SubmitHandler } from "react-hook-form"
import FormTextField from "../atoms/FormTextField"
import { createContactMessage } from "../../services/contact.service"
import { useState } from "react"

interface ContactFormInputs {
  name: string
  email: string
  phone: string
  message: string
}

const ContactForm: React.FC = () => {

  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ContactFormInputs>({
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      message: "",
    },
  })

  const onSubmit: SubmitHandler<ContactFormInputs> = async (data) => {
    try {
      setLoading(true);
      await createContactMessage({ name: data.name, email: data.email, phone: data.phone, message: data.message })
      setSuccess(true)
      setTimeout(()=> {
        setSuccess(false);
      }, 3000);
    } catch (err) {
      setError("Failed to send message. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Your message has been sent successfully! We'll get back to you soon.
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Controller
            name="name"
            control={control}
            rules={{ required: "Name is required" }}
            render={({ field }) => (
              <FormTextField
                {...field}
                label="Your Name"
                fullWidth
                error={!!errors.name}
                helperText={errors.name?.message}
                variant="filled"
                InputProps={{ style: { backgroundColor: "rgba(255, 255, 255, 0.1)", color: "white" } }}
                InputLabelProps={{ style: { color: "rgba(255, 255, 255, 0.7)" } }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="email"
            control={control}
            rules={{
              required: "Email is required",
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: "Invalid email address",
              },
            }}
            render={({ field }) => (
              <FormTextField
                {...field}
                label="Email Address"
                fullWidth
                error={!!errors.email}
                helperText={errors.email?.message}
                variant="filled"
                InputProps={{ style: { backgroundColor: "rgba(255, 255, 255, 0.1)", color: "white" } }}
                InputLabelProps={{ style: { color: "rgba(255, 255, 255, 0.7)" } }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="phone"
            control={control}
            rules={{
              required: "Phone number is required",
              pattern: {
                value: /^[0-9]{10}$/,
                message: "Please enter a valid 10-digit phone number",
              },
            }}
            render={({ field }) => (
              <FormTextField
                {...field}
                label="Phone Number"
                fullWidth
                error={!!errors.phone}
                helperText={errors.phone?.message}
                variant="filled"
                InputProps={{ style: { backgroundColor: "rgba(255, 255, 255, 0.1)", color: "white" } }}
                InputLabelProps={{ style: { color: "rgba(255, 255, 255, 0.7)" } }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            name="message"
            control={control}
            rules={{ required: "Message is required" }}
            render={({ field }) => (
              <FormTextField
                {...field}
                label="Your Message"
                fullWidth
                multiline
                rows={4}
                error={!!errors.message}
                helperText={errors.message?.message}
                variant="filled"
                InputProps={{ style: { backgroundColor: "rgba(255, 255, 255, 0.1)", color: "white" } }}
                InputLabelProps={{ style: { color: "rgba(255, 255, 255, 0.7)" } }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Button type="submit" variant="contained" color="secondary" fullWidth size="large" sx={{ py: 1.5 }} disabled={loading}>
            {loading&& (<CircularProgress color="primary" />)} Send Message
          </Button>
        </Grid>
      </Grid>
    </Box>
  )
}

export default ContactForm
